import {
  Card,
  SelectType,
  Spinner,
  Text,
} from "@appquality/appquality-design-system";
import { useTranslation } from "react-i18next";
import {
  CampaignAdditionalField,
  useGetCampaignsByCampaignBugTypesQuery,
  useGetCampaignsByCampaignReplicabilitiesQuery,
  useGetCampaignsByCampaignSeveritiesQuery,
  useGetUsersMeCampaignsByCampaignIdBugsAndBugIdQuery,
  useGetUsersMeCampaignsByCampaignIdBugsAndBugIdTasksQuery,
  useGetUsersMeQuery,
} from "../../../services/tryberApi";
import {
  EmptyState,
  LoadingContainer,
  ScrollPane,
  SectionDivider,
} from "../styled";
import { isBugEditable } from "../utils";
import BugCampaignFields from "./BugCampaignFields";
import BugDetailHeader from "./BugDetailHeader";
import BugDeviceSection from "./BugDeviceSection";
import BugMediaSection from "./BugMediaSection";
import BugMetadata from "./BugMetadata";
import BugTextSection from "./BugTextSection";
import { BugData } from "./types";
import { useBugEdit } from "./useBugEdit";
import { addMessage } from "../../../redux/siteWideMessages/actionCreators";
import { useAppDispatch } from "../../../redux/provider";

interface BugDetailProps {
  campaignId: string;
  bugId?: number;
  hasBugParade: number;
  minimumMedia: number;
  additionalFields?: CampaignAdditionalField[];
  validFileExtensions?: string[];
}

const BugDetail = ({
  campaignId,
  bugId,
  hasBugParade,
  minimumMedia,
  additionalFields,
  validFileExtensions,
}: BugDetailProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data, error, isLoading, refetch } =
    useGetUsersMeCampaignsByCampaignIdBugsAndBugIdQuery(
      { campaignId, bugId: bugId?.toString() || "" },
      { skip: !bugId },
    );
  const { data: currentUser } = useGetUsersMeQuery({ fields: "id" });

  const { data: campaignUsecases } =
    useGetUsersMeCampaignsByCampaignIdBugsAndBugIdTasksQuery(
      { campaignId: campaignId, bugId: bugId?.toString() || "" },
      { skip: !campaignId || !bugId },
    );
  const { data: campaignSeverities } = useGetCampaignsByCampaignSeveritiesQuery(
    { campaign: campaignId },
    { skip: !campaignId },
  );
  const { data: campaignReplicabilities } =
    useGetCampaignsByCampaignReplicabilitiesQuery(
      { campaign: campaignId },
      { skip: !campaignId },
    );
  const { data: campaignBugTypes } = useGetCampaignsByCampaignBugTypesQuery(
    { campaign: campaignId },
    { skip: !campaignId },
  );

  const {
    isEditing,
    isSaving,
    isUploading,
    editedValues,
    mediaToRemove,
    newMediaFiles,
    totalMediaCount,
    isMediaValid,
    startEditing,
    handleCancel,
    handleSave,
    patchField,
    setMediaToRemove,
    setNewMediaFiles,
  } = useBugEdit({
    campaignId,
    bugId,
    currentMedia: data?.media || [],
    minimumMedia,
    onSaved: async () => {
      await refetch();
    },
    onSuccess: () => {
      dispatch(
        addMessage(t<string>("Bug details updated successfully."), "success"),
      );
    },
    onError: (error) => {
      dispatch(
        addMessage(
          t<string>("Failed to update bug. Please try again."),
          "danger",
        ),
      );
    },
  });

  if (!bugId) {
    return (
      <EmptyState>
        <Text>{t("Select a bug from the list to view details")}</Text>
      </EmptyState>
    );
  }

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (error || !data) {
    return (
      <EmptyState>
        <Text className="aq-text-danger">
          {t("Error loading bug details. Please try again.")}
        </Text>
      </EmptyState>
    );
  }

  const bug = data as BugData;

  const availableSeverities: SelectType.Option[] =
    campaignSeverities?.map((s) => ({
      label: s.name,
      value: s.id.toString(),
    })) || [];

  const availableTypes: SelectType.Option[] =
    campaignBugTypes?.map((ty) => ({
      label: ty.name,
      value: ty.id.toString(),
    })) || [];

  const availableReplicabilities: SelectType.Option[] =
    campaignReplicabilities?.map((r) => ({
      label: r.name,
      value: r.id.toString(),
    })) || [];

  const availableUsecases: SelectType.Option[] =
    campaignUsecases?.map((u) => ({
      label: u.name,
      value: (u.id ?? "").toString(),
    })) || [];

  availableUsecases.push({ label: t("Not a specific use case"), value: "-1" });

  const inOptions = (options: SelectType.Option[], id: number) =>
    options.some((o) => o.value === id.toString());

  // only the edited fields are sent on save, so validate just those: a bug
  // whose original value is no longer allowed by the campaign stays editable
  const isEditedValueValid = (
    options: SelectType.Option[],
    edited: { id: number } | undefined,
  ) => edited === undefined || inOptions(options, edited.id);

  const isMetadataValid =
    isEditedValueValid(availableSeverities, editedValues.severity) &&
    isEditedValueValid(availableTypes, editedValues.type) &&
    isEditedValueValid(availableReplicabilities, editedValues.replicability) &&
    isEditedValueValid(availableUsecases, editedValues.usecase);

  const isBugParade = hasBugParade === 1;
  const isOwnBug = currentUser?.id === bug.tester.id;
  const isEditable =
    isBugEditable(bug.status?.name) && (!isBugParade || isOwnBug);

  const toggleRemoveMedia = (mediaId: number) => {
    setMediaToRemove((prev) =>
      prev.includes(mediaId) ? prev : [...prev, mediaId],
    );
  };

  return (
    <ScrollPane>
      <Card className="aq-mb-3" bodyClass="aq-p-4">
        <BugDetailHeader
          data={bug}
          isEditing={isEditing}
          isEditable={isEditable}
          isSaving={isSaving}
          isMediaValid={isMediaValid}
          isMetadataValid={isMetadataValid}
          minimumMedia={minimumMedia}
          editedValues={editedValues}
          onStartEditing={startEditing}
          onSave={handleSave}
          onCancel={handleCancel}
          onTitleChange={(title) => patchField("title", title)}
        />

        <SectionDivider />

        <BugMetadata
          data={bug}
          isEditing={isEditing}
          editedValues={editedValues}
          availableSeverities={availableSeverities}
          availableTypes={availableTypes}
          availableReplicabilities={availableReplicabilities}
          availableUsecases={availableUsecases}
          onChange={patchField}
        />

        {bug.campaign_fields && bug.campaign_fields.length > 0 && (
          <>
            <SectionDivider />
            <BugCampaignFields
              data={bug}
              isEditing={isEditing}
              editedValues={editedValues}
              additionalFields={additionalFields}
              onChange={(fields) => patchField("campaign_fields", fields)}
            />
          </>
        )}

        {bug.tester.device && (
          <>
            <SectionDivider />
            <BugDeviceSection data={bug} />
          </>
        )}

        <SectionDivider />

        <BugTextSection
          title={t("Description")}
          isEditing={isEditing}
          value={editedValues.description ?? bug.description ?? ""}
          onChange={(val) => patchField("description", val)}
        />

        <BugTextSection
          title={t("Expected Result")}
          isEditing={isEditing}
          value={editedValues.expected_result ?? bug.expected_result ?? ""}
          onChange={(val) => patchField("expected_result", val)}
        />

        <BugTextSection
          title={t("Actual Result")}
          isEditing={isEditing}
          value={editedValues.actual_result ?? bug.actual_result ?? ""}
          onChange={(val) => patchField("actual_result", val)}
        />

        <BugTextSection
          title={t("Additional Comments")}
          isEditing={isEditing}
          value={editedValues.comment ?? bug.note ?? ""}
          emptyPlaceholder={t(
            "No additional comments. Click Edit to add some.",
          )}
          onChange={(val) => patchField("comment", val)}
        />

        <BugMediaSection
          data={bug}
          isEditing={isEditing}
          isUploading={isUploading}
          mediaToRemove={mediaToRemove}
          newMediaFiles={newMediaFiles}
          totalMediaCount={totalMediaCount}
          isMediaValid={isMediaValid}
          minimumMedia={minimumMedia}
          validFileExtensions={validFileExtensions}
          onToggleRemove={toggleRemoveMedia}
          onAddFiles={(files) =>
            setNewMediaFiles((prev) => [...prev, ...files])
          }
          onRemoveNewFile={(index) =>
            setNewMediaFiles((prev) => prev.filter((_, i) => i !== index))
          }
        />
      </Card>
    </ScrollPane>
  );
};

export default BugDetail;
